import * as React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import {
  FILTER_DEFAULT_VISIBILITY_LABELS,
  FILTER_SELECTION_MODE_LABELS,
  FILTER_SOURCE_LABELS,
} from "./enum-labels";
import {
  FilterDefaultVisibility,
  FilterSelectionMode,
  FilterShapeDetail,
  FilterSource,
  FilterTypeDetail,
} from "./types";

export type FilterTypeFormValues = Partial<FilterTypeDetail>;

export interface FilterTypeFormState {
  values: FilterTypeFormValues;
  errors: Partial<Record<keyof FilterTypeFormValues, string>>;
}

export type FormFieldValue = string | number | boolean | undefined;

export interface ValidationResult {
  issues: { message: string; path: (keyof FilterTypeFormValues)[] }[];
}

export const validateFilterType = (
  values: FilterTypeFormValues,
  isEdit: boolean,
): ValidationResult => {
  let issues: ValidationResult["issues"] = [];

  if (!isEdit && !values.code?.trim()) {
    issues = [...issues, { message: "Code is required", path: ["code"] }];
  }

  if (!values.label?.trim()) {
    issues = [...issues, { message: "Label is required", path: ["label"] }];
  }

  if (!values.shapeId) {
    issues = [...issues, { message: "Shape is required", path: ["shapeId"] }];
  }

  if (!values.source) {
    issues = [...issues, { message: "Source is required", path: ["source"] }];
  }

  return { issues };
};

export interface FilterTypeFormProps {
  formState: FilterTypeFormState;
  shapes: FilterShapeDetail[];
  isEdit: boolean;
  onFieldChange: (
    name: keyof FilterTypeFormValues,
    value: FormFieldValue,
  ) => void;
  onSubmit: () => void;
}

const FilterTypeForm = (props: FilterTypeFormProps) => {
  const { formState, shapes, isEdit, onFieldChange, onSubmit } = props;
  const formValues = formState.values;
  const formErrors = formState.errors;

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof FilterTypeFormValues,
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleNumberFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      onFieldChange(
        name as keyof FilterTypeFormValues,
        value === "" ? undefined : Number(value),
      );
    },
    [onFieldChange],
  );

  const handleCheckboxFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onFieldChange(event.target.name as keyof FilterTypeFormValues, checked);
    },
    [onFieldChange],
  );

  const handleSelectFieldChange = React.useCallback(
    (event: SelectChangeEvent<number>) => {
      onFieldChange(
        event.target.name as keyof FilterTypeFormValues,
        Number(event.target.value),
      );
    },
    [onFieldChange],
  );

  return (
    <Box
      component="form"
      aria-label="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      noValidate
      autoComplete="off"
      sx={{ width: "100%" }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ width: "100%", pt: 1 }}>
          {!isEdit && (
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
              <TextField
                value={formValues.code ?? ""}
                onChange={handleTextFieldChange}
                name="code"
                label="Code"
                error={!!formErrors.code}
                helperText={formErrors.code ?? " "}
                fullWidth
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: isEdit ? 12 : 6 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.label ?? ""}
              onChange={handleTextFieldChange}
              name="label"
              label="Label"
              error={!!formErrors.label}
              helperText={formErrors.label ?? " "}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <FormControl error={!!formErrors.shapeId} fullWidth>
              <InputLabel id="filter-type-shape-label">Shape</InputLabel>
              <Select
                value={formValues.shapeId ?? ""}
                onChange={handleSelectFieldChange as SelectProps["onChange"]}
                labelId="filter-type-shape-label"
                name="shapeId"
                label="Shape"
                fullWidth
              >
                {shapes.map((shape) => (
                  <MenuItem key={shape.id} value={shape.id}>
                    {shape.code}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{formErrors.shapeId ?? " "}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <FormControl error={!!formErrors.source} fullWidth>
              <InputLabel id="filter-type-source-label">Source</InputLabel>
              <Select
                value={formValues.source ?? ""}
                onChange={handleSelectFieldChange as SelectProps["onChange"]}
                labelId="filter-type-source-label"
                name="source"
                label="Source"
                fullWidth
              >
                {Object.entries(FILTER_SOURCE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value) as FilterSource}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{formErrors.source ?? " "}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <FormControl fullWidth>
              <InputLabel id="filter-type-selection-mode-label">
                Selection Mode
              </InputLabel>
              <Select
                value={formValues.selectionMode ?? FilterSelectionMode.Multi}
                onChange={handleSelectFieldChange as SelectProps["onChange"]}
                labelId="filter-type-selection-mode-label"
                name="selectionMode"
                label="Selection Mode"
                fullWidth
              >
                {Object.entries(FILTER_SELECTION_MODE_LABELS).map(
                  ([value, label]) => (
                    <MenuItem
                      key={value}
                      value={Number(value) as FilterSelectionMode}
                    >
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <FormControl fullWidth>
              <InputLabel id="filter-type-default-visibility-label">
                Default Visibility
              </InputLabel>
              <Select
                value={
                  formValues.defaultVisibility ??
                  FilterDefaultVisibility.VisibleToAll
                }
                onChange={handleSelectFieldChange as SelectProps["onChange"]}
                labelId="filter-type-default-visibility-label"
                name="defaultVisibility"
                label="Default Visibility"
                fullWidth
              >
                {Object.entries(FILTER_DEFAULT_VISIBILITY_LABELS).map(
                  ([value, label]) => (
                    <MenuItem
                      key={value}
                      value={Number(value) as FilterDefaultVisibility}
                    >
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.sortOrder ?? 0}
              onChange={handleNumberFieldChange}
              name="sortOrder"
              label="Sort Order"
              type="number"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.targetField ?? ""}
              onChange={handleTextFieldChange}
              name="targetField"
              label="Target Field"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.targetColumn ?? ""}
              onChange={handleTextFieldChange}
              name="targetColumn"
              label="Target Column"
              fullWidth
            />
          </Grid>
          <Grid
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            {isEdit && (
              <FormControl>
                <FormControlLabel
                  name="isSystem"
                  control={
                    <Checkbox
                      checked={formValues.isSystem ?? false}
                      onChange={handleCheckboxFieldChange}
                    />
                  }
                  label="System"
                />
              </FormControl>
            )}
            <FormControl>
              <FormControlLabel
                name="isDropdown"
                control={
                  <Checkbox
                    checked={formValues.isDropdown ?? false}
                    onChange={handleCheckboxFieldChange}
                  />
                }
                label="Dropdown"
              />
            </FormControl>
            <FormControl>
              <FormControlLabel
                name="isEnabled"
                control={
                  <Checkbox
                    checked={formValues.isEnabled ?? true}
                    onChange={handleCheckboxFieldChange}
                  />
                }
                label="Enabled"
              />
            </FormControl>
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );
};

export default FilterTypeForm;
